// ── GQ shared UI utilities ────────────────────────────────────────────
// One home for the small primitives that every page needs:
//   - seededRng / sparkFromSeed / sparkFromEquity (replaces 3 duplicates)
//   - navigateTo                                  (replaces 7 fallback dances)
//   - formatPct / formatMoney / formatBps         (one consistent formatter)
//   - PLOTLY_LAYOUT_BASE / PLOTLY_CONFIG          (re-exported from charts.jsx)
//
// Attached to window.GQ.utils.* and (for the most-used helpers) also as
// direct window.* globals so existing call sites can adopt incrementally.

(function (root) {
  // ────────────────────────────────────────────────────────────────────
  // Seeded RNG — Park-Miller LCG, identical to what vault.jsx / tearsheet.jsx
  // had inlined. Returns a closure that yields values in [0, 1).
  // ────────────────────────────────────────────────────────────────────
  function seededRng(seed) {
    let s = seed | 0;
    if (s <= 0) s = 1;
    return () => {
      s = (s * 16807) % 2147483647;
      return (s - 1) / 2147483646;
    };
  }

  // 100-anchored compounding walk — used by every "fake sparkline" call site.
  function sparkFromSeed(seed, length = 18, vol = 0.035, mean = 0.46) {
    const r = seededRng(seed);
    let v = 100;
    return Array.from({ length }, () => { v *= 1 + (r() - mean) * vol; return v; });
  }

  // Downsample a real equity_curve [{t,v}, …] (or [v, …]) to N points.
  function sparkFromEquity(equityCurve, n = 18) {
    if (!Array.isArray(equityCurve) || equityCurve.length === 0) return null;
    if (equityCurve.length <= n) return equityCurve.map(p => p?.v ?? p);
    const stride = (equityCurve.length - 1) / (n - 1);
    return Array.from({ length: n }, (_, i) => {
      const idx = Math.min(equityCurve.length - 1, Math.round(i * stride));
      return equityCurve[idx]?.v ?? equityCurve[idx];
    });
  }

  // ────────────────────────────────────────────────────────────────────
  // Navigation — single funnel that all pages should call.
  // Replaces  `if (window.GQ?.state?.setHash) ... else (location.hash = ...)`
  // ────────────────────────────────────────────────────────────────────
  function navigateTo(page, params = {}) {
    if (root.GQ?.state?.setHash) {
      root.GQ.state.setHash(page, params);
      return;
    }
    const qs = Object.entries(params)
      .filter(([_, v]) => v != null && v !== '')
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
      .join('&');
    root.location.hash = qs ? `${page}?${qs}` : page;
  }

  // ────────────────────────────────────────────────────────────────────
  // Formatters — keep display consistent across every KPI strip, badge,
  // tooltip and table cell.
  // ────────────────────────────────────────────────────────────────────
  function formatPct(v, dp = 1, withSign = true) {
    if (v == null || Number.isNaN(v)) return '—';
    const n = v * 100;
    return (withSign && n >= 0 ? '+' : '') + n.toFixed(dp) + '%';
  }
  function formatMoney(v, dp = 0) {
    if (v == null || Number.isNaN(v)) return '—';
    const abs = Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: dp, maximumFractionDigits: dp });
    return (v >= 0 ? '+$' : '-$') + abs;
  }
  function formatBps(v, dp = 1) {
    if (v == null || Number.isNaN(v)) return '—';
    return (v * 10000).toFixed(dp) + ' bps';
  }
  function formatNumber(v, dp = 2) {
    if (v == null || Number.isNaN(v)) return '—';
    return v.toFixed(dp);
  }

  // ────────────────────────────────────────────────────────────────────
  // Public surface
  // ────────────────────────────────────────────────────────────────────
  root.GQ = root.GQ || {};
  root.GQ.utils = {
    seededRng, sparkFromSeed, sparkFromEquity,
    navigateTo,
    formatPct, formatMoney, formatBps, formatNumber,
  };

  // Also as direct globals for the most-used helpers (incremental adoption).
  root.navigateTo      = navigateTo;
  root.sparkFromSeed   = sparkFromSeed;
  root.sparkFromEquity = sparkFromEquity;
  root.formatPct       = formatPct;
  root.formatMoney     = formatMoney;
  root.formatBps       = formatBps;
})(typeof window !== 'undefined' ? window : globalThis);
